import React, { useEffect } from "react";
import "../styles/styles.css";

function PrivacyPolicy() {
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="privacy-section">
      <div
        className="privacy-content"
        style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
      >
        <h1>Privacy Policy & Terms of Service</h1>
        <p className="last-updated">Last updated: May {currentYear}</p>

        <p>
          This document describes how AccessPie Inc. (“AccessPie”, “we”, “us”,
          or “our”) operates the Margin application (“Margin” or the “App”),
          including how we collect, use, and protect your information, as well
          as the terms governing your use of the service.
        </p>

        {/* ===================== PRIVACY POLICY ===================== */}
        <h2>Privacy Policy</h2>

        <h3>1. Information We Collect</h3>

        <div>
          <h4>1.1 Account Information</h4>
          <p>
            When you create an account, we collect your email address and a
            securely hashed password. You may optionally provide a display name
            to personalize your experience.
          </p>
        </div>

        <div>
          <h4>1.2 User Content</h4>
          <p>
            <strong>Notes and Voice Recordings:</strong> We store notes and
            voice recordings you create. Voice recordings may be processed to
            generate text transcripts.
          </p>
          <p>
            <strong>Application Data:</strong> This includes titles, tags, and
            other content you create within the App to support core
            functionality.
          </p>
        </div>

        <div>
          <h4>1.3 Device Permissions</h4>
          <p>
            <strong>Microphone:</strong> Used only when you initiate a
            recording.
          </p>
          <p>
            <strong>Notifications:</strong> Optional and used for reminders and
            service updates.
          </p>
        </div>

        <div>
          <h4>1.4 Technical Data</h4>
          <p>
            We may collect limited device and usage data (such as device type,
            operating system, and crash logs) to improve performance and
            security.
          </p>
        </div>

        <h3>2. How We Use Information</h3>
        <ul>
          <li>Provide and operate the Margin application</li>
          <li>Sync data across devices</li>
          <li>Generate and improve transcriptions</li>
          <li>Maintain security and prevent abuse</li>
          <li>Send essential service communications</li>
        </ul>

        <p>
          We may use your input, such as reading interests or content you
          create, to provide personalized recommendations (for example,
          suggesting books or other content you may find useful). Some
          recommendations may include links to third-party services. If you make
          a purchase through these links, we may earn a commission at no
          additional cost to you.
        </p>

        <p>
          We do not sell personal data. We do not use your content to train
          public AI models.
        </p>

        <h3>3. Data Sharing</h3>
        <p>
          We do not sell or rent personal information. We may share data only:
        </p>
        <ul>
          <li>With service providers (hosting, infrastructure, AI APIs)</li>
          <li>When required by law or legal process</li>
          <li>To protect the safety and integrity of our service</li>
        </ul>

        <h3>4. Data Retention</h3>
        <p>
          We retain data while your account is active. Upon deletion, data is
          removed from active systems within a reasonable period, subject to
          legal obligations.
        </p>

        <h3>5. Security</h3>
        <p>
          We use industry-standard security measures including encryption in
          transit and at rest. However, no system is completely secure.
        </p>

        <h3>6. Your Rights</h3>
        <ul>
          <li>Access your data</li>
          <li>Request correction or deletion</li>
          <li>Export your data</li>
          <li>Withdraw consent where applicable</li>
        </ul>

        <h3>7. International Users</h3>
        <p>
          Your data may be processed in Canada or other jurisdictions where our
          service providers operate.
        </p>

        <h3>8. Changes to This Policy</h3>
        <p>
          We may update this Policy from time to time. Continued use of the App
          means you accept any updates.
        </p>

        <h3>9. Contact</h3>
        <p>
          Contact us via:
          <br />
          <a href="https://linkedin.com/company/accesspie-margin/">LinkedIn</a>
        </p>

        {/* ===================== TERMS ===================== */}
        <h2>Terms of Service</h2>

        <h3>10. Acceptance of Terms</h3>
        <p>
          By using Margin, you agree to these Terms. If you do not agree, you
          may not use the App.
        </p>

        <h3>11. License</h3>
        <p>
          We grant you a limited, non-exclusive, non-transferable, revocable
          license to use the App for personal or business productivity.
        </p>

        <h3>12. Acceptable Use</h3>
        <p>You agree not to:</p>
        <ul>
          <li>Use the service for illegal or harmful activity</li>
          <li>Reverse engineer, copy, or resell the App</li>
          <li>Use bots, scraping, or automated bulk access</li>
          <li>Abuse or overload AI/transcription systems</li>
          <li>Disrupt or interfere with normal service operation</li>
        </ul>

        <h3>13. Usage Limits & Fair Use</h3>
        <p>
          We may enforce usage limits to ensure fair access and system
          stability. If we detect abuse, excessive usage, or automated behavior,
          we may limit or suspend access.
        </p>

        <h3>14. Suspension & Termination</h3>
        <p>
          We may suspend or terminate accounts if we reasonably believe these
          Terms have been violated, or if usage creates risk, abuse, or
          excessive cost.
        </p>

        <h3>15. Disclaimer</h3>
        <p>
          The service is provided “as is” without warranties of any kind. We do
          not guarantee uninterrupted service or error-free transcription.
        </p>

        <h3>16. Limitation of Liability</h3>
        <p>
          To the maximum extent permitted by law, AccessPie Inc. is not liable
          for indirect, incidental, or consequential damages arising from use of
          the App.
        </p>

        <h3>17. Updates to Terms</h3>
        <p>
          We may update these Terms. Continued use of the App constitutes
          acceptance of changes.
        </p>

        <div className="policy-footer">
          <p>
            By using Margin, you acknowledge that you have read and agree to
            this Privacy Policy and Terms of Service.
          </p>
        </div>
      </div>
    </section>
  );
}

export default PrivacyPolicy;
